import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import Carousel from '../../components/Carousel';
import './InstagramReels.css';

const INSTAGRAM_URL = 'https://www.instagram.com/podiatech.uz?igsh=N2hyeDZvdHAyeHl6';
const extractReelId = (value = '') => {
    const match = value.match(/\/reel\/([^/?#]+)/);
    return match?.[1] || '';
};

const normalizeReels = (items) =>
    items
        .map((item, index) => {
            const reelId =
                item?.reel_id
                || extractReelId(item?.url || '')
                || extractReelId(item?.embed_url || '');
            const url = item?.url || (reelId ? `https://www.instagram.com/reel/${reelId}/` : '');
            const embedUrl = item?.embed_url || (reelId ? `https://www.instagram.com/reel/${reelId}/embed` : '');

            return {
                id: item?.id ?? reelId ?? `reel-${index}`,
                url,
                embedUrl,
                title: item?.title || '',
                order: item?.order ?? index
            };
        })
        .filter((item) => item.url && item.embedUrl)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

const InstagramReels = () => {
    const { t } = useTranslation();
    const [reels, setReels] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDesktop, setIsDesktop] = useState(false);

    const baseUrl = useMemo(
        () => import.meta.env.VITE_API_BASE_URL?.replace(/\/+$/, ''),
        []
    );

    useEffect(() => {
        const controller = new AbortController();

        if (!baseUrl) {
            console.error('Missing VITE_API_BASE_URL');
            setIsLoading(false);
            return () => controller.abort();
        }

        const loadReels = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(`${baseUrl}/api/v1/videos/instagram-reels/`, {
                    signal: controller.signal
                });

                if (!response.ok) {
                    throw new Error(`Instagram reels request failed: ${response.status}`);
                }

                const data = await response.json();
                const results = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.results)
                        ? data.results
                        : [];

                setReels(normalizeReels(results));
            } catch (error) {
                if (error.name === 'AbortError') {
                    return;
                }
                console.error(error.message || 'Failed to load Instagram reels');
                setReels([]);
            } finally {
                setIsLoading(false);
            }
        };

        loadReels();

        return () => controller.abort();
    }, [baseUrl]);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(min-width: 768px)');
        const updateViewport = () => setIsDesktop(mediaQuery.matches);

        updateViewport();
        mediaQuery.addEventListener('change', updateViewport);

        return () => mediaQuery.removeEventListener('change', updateViewport);
    }, []);

    const hasReels = reels.length > 0;

    const ReelItem = ({ reel, index }) => (
        <a
            href={reel.url}
            target="_blank"
            rel="noopener noreferrer"
            className="instagram-reels__link"
        >
            <div className="instagram-reels__embed-wrapper">
                <iframe
                    src={reel.embedUrl}
                    title={reel.title || `Instagram Reel ${index + 1}`}
                    scrolling="no"
                    loading="lazy"
                    fetchpriority="low"
                    referrerPolicy="strict-origin-when-cross-origin"
                />
            </div>
        </a>
    );

    return (
        <section className="instagram-reels" id="instagram-reels">
            <div className="instagram-reels__container">
                <motion.div
                    className="instagram-reels__header"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <h2 className="instagram-reels__title">
                        <span className="instagram-reels__icon">
                            <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                            </svg>
                        </span>
                        {t('instagram_reels.title')}
                    </h2>
                    <p className="instagram-reels__subtitle">{t('instagram_reels.subtitle')}</p>
                </motion.div>

                {isDesktop ? (
                    <div className="instagram-reels__grid instagram-reels__grid--desktop">
                        {isLoading ? (
                            <p className="instagram-reels__status">Loading...</p>
                        ) : hasReels ? (
                            reels.map((reel, index) => (
                                <motion.div
                                    key={reel.id}
                                    className="instagram-reels__item"
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                >
                                    <ReelItem reel={reel} index={index} />
                                </motion.div>
                            ))
                        ) : (
                            <p className="instagram-reels__status">No reels available.</p>
                        )}
                    </div>
                ) : (
                    <div className="instagram-reels__carousel instagram-reels__carousel--mobile">
                        {isLoading ? (
                            <p className="instagram-reels__status">Loading...</p>
                        ) : hasReels ? (
                            <Carousel showArrows={false}>
                                {reels.map((reel, index) => (
                                    <div key={reel.id} className="instagram-reels__item">
                                        <ReelItem reel={reel} index={index} />
                                    </div>
                                ))}
                            </Carousel>
                        ) : (
                            <p className="instagram-reels__status">No reels available.</p>
                        )}
                    </div>
                )}

                <motion.div
                    className="instagram-reels__cta"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.4 }}
                >
                    <a
                        href={INSTAGRAM_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="instagram-reels__follow-btn"
                    >
                        <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                        </svg>
                        @podiatech.uz
                    </a>
                </motion.div>
            </div>
        </section>
    );
};

export default InstagramReels;
